import { Package, PlusIcon, Trash2 } from "@/components/icons";
import { Button, Input } from "@/components/kit";
import {
  FieldContent,
  FieldLabel,
  Field as FieldLayout,
} from "@/components/ui/field";
import type { PickedVariant } from "@/modules/orders/presentation/ui/ProductPickerModal";
import { formatDateLocal } from "@/shared/lib/date-time";
import type { RestockWaitlistImportHintDTO } from "../../domain/types";

export type WaitlistFormLine = {
  lineKey: string;
  variant: PickedVariant;
  quantity: number;
};

type WaitlistLinesEditorProps = {
  lines: WaitlistFormLine[];
  importHintsByVariant?: Record<string, RestockWaitlistImportHintDTO | undefined>;
  importHintsLoading?: boolean;
  onAddClick: () => void;
  onRemoveLine: (lineKey: string) => void;
  onQuantityChange: (lineKey: string, quantity: number) => void;
};

export function WaitlistLinesEditor({
  lines,
  importHintsByVariant,
  importHintsLoading,
  onAddClick,
  onRemoveLine,
  onQuantityChange,
}: WaitlistLinesEditorProps) {
  return (
    <div className="flex flex-col gap-3">
      {lines.length === 0 ? (
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-full"
          onClick={onAddClick}
        >
          <PlusIcon data-icon="inline-start" />
          ເລືອກສິນຄ້າ
        </Button>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {lines.map((line) => {
              const hint = importHintsByVariant?.[line.variant.variantId];
              return (
                <li
                  key={line.lineKey}
                  className="brand-card flex flex-col gap-3 px-4 py-3"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-secondary">
                      <Package className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm">
                        {line.variant.productName}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {line.variant.variantName}
                      </p>
                      <p className="mt-1 text-muted-foreground text-xs">
                        ສະຕັອກປັດຈຸບັນ {line.variant.stockQuantity}{" "}
                        {line.variant.stockUnitLabel}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0 rounded-full text-muted-foreground"
                      onClick={() => onRemoveLine(line.lineKey)}
                      aria-label="ລຶບລາຍການ"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>

                  {importHintsLoading ? (
                    <p className="text-muted-foreground text-xs">ກຳລັງໂຫຼດ import…</p>
                  ) : hint ? (
                    <div className="rounded-xl border border-border/60 bg-muted/30 px-3 py-2 text-xs">
                      ກຳລັງນຳເຂົ້າ {hint.incomingQuantity} ຊິ້ນ
                      {hint.nearestExpectedAt
                        ? ` · ຄາດຮອດ ${formatDateLocal(hint.nearestExpectedAt)}`
                        : ""}
                    </div>
                  ) : null}

                  <FieldLayout>
                    <FieldLabel>ຈຳນວນ</FieldLabel>
                    <FieldContent>
                      <Input
                        type="number"
                        min={1}
                        className="rounded-xl"
                        value={line.quantity}
                        onChange={(e) =>
                          onQuantityChange(
                            line.lineKey,
                            Math.max(1, Number(e.target.value) || 1),
                          )
                        }
                      />
                    </FieldContent>
                  </FieldLayout>
                </li>
              );
            })}
          </ul>

          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={onAddClick}
          >
            <PlusIcon data-icon="inline-start" />
            ເພີ່ມສິນຄ້າ
          </Button>
        </>
      )}
    </div>
  );
}

export function waitlistFormLineFromPick(
  variant: PickedVariant,
  quantity = 1,
): WaitlistFormLine {
  return {
    lineKey: `${variant.variantId}-${crypto.randomUUID()}`,
    variant,
    quantity,
  };
}
